// `@hello-pangea/dnd` é fork mantido do `react-beautiful-dnd` com suporte a
// React 18 (RBD original quebra com "Cannot find droppable entry" em vários
// cenários de re-render). API 100% compatível.
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useEffect, useRef, useState } from 'react';
import { DotsSixVertical, Plus, X } from 'shared/icons';
import ConfirmModal from '../../../../../components/ui/ConfirmModal';
import {
    CHARTABLE_COLS,
    COL_DEFS,
    IsNumericColumn as isNumericColumn,
    SELECTABLE_COLS,
} from '../../../constants/columns';
import {
    EXPRESSION_FUNCTIONS,
    EXPRESSION_VARIABLES,
    FormatExpressionValue,
    ValidateExpression,
} from '../../../expressionEngine';
import { TBtn, TInput, useClickOutside } from '../../primitives';
import { useTokens } from '../../tokens';
import { PSection, usePresetBuilder } from '../context.jsx';
import { ViewModeToggle } from './ChartSettings.jsx';

const FORMAT_OPTS = [
    { id: 'number', label: 'Número' },
    { id: 'percentage', label: 'Porcentagem' },
    { id: 'currency', label: 'Moeda (R$)' },
];

// `name` é a versão LONGA (lista de colunas, picker, exports); `label` é a
// CURTA usada no header da tabela. Ex: name='Mercado Livre disponível',
// label='ML disp.'. Schema centralizado em data.js — sem fallbacks.
const colLabel = (col) => COL_DEFS[col]?.name || col;

// Convenção de refs pra colunas customizadas globais. Backend e frontend
// concordam em "custom:<uuid>". Mantemos string em vez de objeto pra ficar
// homogêneo com built-ins ('sku', 'name', ...) no array preset.columns.
const CUSTOM_REF_PREFIX = 'custom:';
const isCustomRef = (c) => typeof c === 'string' && c.startsWith(CUSTOM_REF_PREFIX);
const refToCustomId = (c) => (isCustomRef(c) ? c.slice(CUSTOM_REF_PREFIX.length) : null);
const idToCustomRef = (id) => `${CUSTOM_REF_PREFIX}${id}`;

export const ColKey = (c) => (typeof c === 'string' ? c : c?.id);
export const ColDisplay = (c, customById) => {
    if (isCustomRef(c)) {
        const id = refToCustomId(c);
        return customById?.get(id)?.name || '(coluna deletada)';
    }
    if (typeof c === 'string') return colLabel(c);
    return c?.name || c?.id || '?';
};

// Colunas são UNIVERSAIS entre identifiers (sku/mlb). Antes filtrávamos pra
// esconder product-level no modo MLB, mas isso violava o modelo:
//   - SKU mode: vendas = soma de todos os MLBs daquele SKU (já era assim)
//   - MLB mode: o anúncio é de UM SKU → herda brand/bling/mlAvail/tags/etc
//     via lookup (ver `buildMlbRow` em useOperacoesData).
// Então TODAS as colunas funcionam em ambas as views — o pipeline preenche.

/** Botão "tracejado + adicionar X" que abre dropdowns inline. */
function AddRow({ label, onClick, refEl }) {
    const T = useTokens();
    return (
        <button
            ref={refEl}
            type="button"
            onClick={onClick}
            style={{
                width: '100%',
                marginTop: 6,
                border: `1px dashed ${T.line}`,
                background: 'transparent',
                color: T.muted,
                padding: '7px 10px',
                fontSize: 11,
                cursor: 'pointer',
                borderRadius: 4,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                outline: 'none',
            }}
        >
            <Plus size={11} /> {label}
        </button>
    );
}

/**
 * Editor de coluna customizada GLOBAL. Mudanças vão pra um draft local; o user
 * dispara save manual (botão "Salvar") que persiste via `onSave(patch)` —
 * propaga pra todos os presets que referenciam essa coluna.
 *
 * Por que NÃO save automático: edição é um evento raro com impacto amplo. Save
 * imediato em cada keystroke spamaria o backend e poderia deixar fórmulas
 * meio-digitadas afetando outros presets do user. Botão explícito = controle.
 */
function ExpressionEditor({ colDef, onSave, onClose, onDelete, saving }) {
    const T = useTokens();
    const [draft, setDraft] = useState(colDef);
    const [showRef, setShowRef] = useState(false);
    // Sincroniza draft se o colDef externo mudar (ex: outro device editou).
    // Comparação por id evita reset enquanto o user digita na mesma coluna.
    useEffect(() => {
        if (colDef?.id !== draft?.id) {
            setDraft(colDef);
        }
    }, [colDef, draft?.id]);
    const dirty =
        draft.name !== colDef.name ||
        (draft.short_label || '') !== (colDef.short_label || '') ||
        draft.expression !== colDef.expression ||
        draft.format !== colDef.format;
    const validation = draft.expression ? ValidateExpression(draft.expression) : null;
    const canSave = Boolean(draft.name?.trim()) && validation?.valid && dirty;
    return (
        <div
            style={{
                border: `1px solid ${T.accent}`,
                borderRadius: 4,
                background: T.bgSoft,
                padding: 10,
                margin: '4px 0 8px 0',
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    color: T.muted,
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                }}
            >
                Coluna global · mudanças afetam todos os presets que usam
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <TInput
                    value={draft.name}
                    onChange={(v) => setDraft({ ...draft, name: v })}
                    placeholder="Nome (lista de colunas, picker)"
                    width="100%"
                />
                <select
                    value={draft.format || 'number'}
                    onChange={(e) => setDraft({ ...draft, format: e.target.value })}
                    style={{
                        padding: '5px 6px',
                        borderRadius: 4,
                        border: `1px solid ${T.line}`,
                        background: T.bg,
                        color: T.ink,
                        fontSize: 11,
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                >
                    {FORMAT_OPTS.map((o) => (
                        <option key={o.id} value={o.id}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>
            <div style={{ marginBottom: 8 }}>
                {/* Label CURTO usado no header da tabela. UPPERCASE forçado
                    porque o header SEMPRE renderiza em UPPERCASE — assim o
                    user vê na hora de digitar como vai ficar. */}
                <TInput
                    value={draft.short_label || ''}
                    onChange={(v) =>
                        setDraft({
                            ...draft,
                            short_label: (v || '').toUpperCase(),
                        })
                    }
                    placeholder="Label curto no header (opcional, ex: ML DISP.)"
                    width="100%"
                />
            </div>
            <textarea
                value={draft.expression || ''}
                onChange={(e) => setDraft({ ...draft, expression: e.target.value })}
                placeholder="Ex: SE(BLING < 20, SALES_7D * 2, SALES_7D)"
                rows={2}
                style={{
                    width: '100%',
                    resize: 'vertical',
                    fontFamily: T.mono,
                    fontSize: 11,
                    padding: '5px 6px',
                    borderRadius: 4,
                    border: `1px solid ${validation ? (validation.valid ? T.ok : T.danger) : T.line}`,
                    background: T.bg,
                    color: T.ink,
                    outline: 'none',
                    boxSizing: 'border-box',
                }}
            />
            {validation && (
                <div
                    style={{
                        marginTop: 4,
                        fontSize: 10,
                        color: validation.valid ? T.ok : T.danger,
                        fontFamily: T.mono,
                    }}
                >
                    {validation.valid
                        ? validation.preview !== null
                            ? `✓ válido · prévia (row vazio): ${FormatExpressionValue(validation.preview, draft.format)}`
                            : '✓ sintaxe válida'
                        : `✗ ${validation.error}`}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                <button
                    type="button"
                    onClick={() => setShowRef((v) => !v)}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: T.accent,
                        fontSize: 10,
                        padding: 0,
                        outline: 'none',
                    }}
                >
                    {showRef ? '▼' : '▶'} Variáveis e funções
                </button>
                <div style={{ flex: 1 }} />
                {onDelete && (
                    <TBtn variant="ghost" size="xs" onClick={() => onDelete(colDef.id)} disabled={saving}>
                        Excluir
                    </TBtn>
                )}
                <TBtn variant="ghost" size="xs" onClick={onClose} disabled={saving}>
                    Fechar
                </TBtn>
                <TBtn
                    variant="primary"
                    size="xs"
                    onClick={() =>
                        onSave({
                            name: draft.name,
                            short_label: draft.short_label?.trim() ? draft.short_label.trim() : null,
                            expression: draft.expression,
                            format: draft.format,
                        })
                    }
                    disabled={!canSave || saving}
                    title={!validation?.valid ? 'Corrija a expressão' : !dirty ? 'Sem alterações' : undefined}
                >
                    {saving ? 'Salvando…' : 'Salvar'}
                </TBtn>
            </div>
            {showRef && (
                <div
                    style={{
                        marginTop: 6,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 6,
                        fontSize: 10,
                        maxHeight: 200,
                        overflowY: 'auto',
                        padding: 6,
                        background: T.bg,
                        border: `1px solid ${T.line}`,
                        borderRadius: 3,
                    }}
                >
                    <div>
                        <div style={{ fontWeight: 700, color: T.muted, marginBottom: 3 }}>Variáveis</div>
                        {EXPRESSION_VARIABLES.map((v) => (
                            <div key={v.name} style={{ padding: '1px 0' }}>
                                <code style={{ color: T.accent, fontFamily: T.mono }}>{v.name}</code>
                                <span style={{ color: T.muted, marginLeft: 4 }}>{v.desc}</span>
                            </div>
                        ))}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, color: T.muted, marginBottom: 3 }}>Funções</div>
                        {EXPRESSION_FUNCTIONS.map((f) => (
                            <div key={f.name} style={{ padding: '1px 0' }}>
                                <code style={{ color: T.accent, fontFamily: T.mono }}>{f.name}</code>
                                <div style={{ color: T.muted, paddingLeft: 8 }}>{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Lista de colunas com drag-to-reorder + remover por item + dropdown "+ adicionar
 * coluna". Em colunas chartable, mostra o ViewModeToggle (importado de
 * ChartSettings — chart-related fica naquele módulo).
 *
 * Custom columns:
 *   - Renderizam pela ref string `custom:<id>` — o nome vem do customColumnsById.
 *   - "+ adicionar coluna" mostra TANTO built-ins quanto custom columns globais
 *     já criadas pra org (que ainda não estão no preset).
 *   - "Nova coluna customizada" cria uma global via onCreateCustomColumn,
 *     adiciona a ref no preset, e abre o editor automaticamente.
 *   - Editar uma custom column dispara onUpdateCustomColumn → propaga pra
 *     todos os presets que referenciam.
 *   - Excluir uma custom column é destrutivo (impacta outros presets) — só
 *     disponível dentro do ExpressionEditor com confirm explícito.
 */
function ColumnsList({
    cols,
    columnViews,
    onColumnViewsChange,
    onChange,
    customColumns,
    customColumnsById,
    customColumnsLoading,
    onCreateCustomColumn,
    onUpdateCustomColumn,
    onDeleteCustomColumn,
}) {
    const T = useTokens();
    const [adding, setAdding] = useState(false);
    const [editingRef, setEditingRef] = useState(null); // ref tipo "custom:<id>"
    const [savingCustom, setSavingCustom] = useState(false);
    const [creating, setCreating] = useState(false);
    const [deletingColId, setDeletingColId] = useState(null); // id da custom col aguardando confirmação
    const addRef = useRef(null);
    useClickOutside(addRef, () => setAdding(false), adding);

    // 'select' (carrinho) é fixo e sempre primeira coluna — não aparece no editor.
    const visible = cols.filter((c) => ColKey(c) !== 'select');

    const used = new Set(visible.map(ColKey));
    const availableBuiltins = SELECTABLE_COLS.filter((c) => !used.has(c));
    const availableCustoms = (customColumns || []).filter((c) => !used.has(idToCustomRef(c.id)));

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const from = result.source.index;
        const to = result.destination.index;
        if (from === to) return;
        const next = [...visible];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onChange(['select', ...next]);
    };

    const removeCol = (idx) => {
        const removed = visible[idx];
        const next = [...visible];
        next.splice(idx, 1);
        onChange(['select', ...next]);
        if (editingRef && removed === editingRef) {
            setEditingRef(null);
        }
    };

    const addCol = (c) => {
        onChange(['select', ...visible, c]);
        setAdding(false);
    };

    // Cria custom column global + adiciona a ref no preset + abre o editor.
    // Loading guard evita criar 2 colunas se o user spammear o botão.
    const addNewCustomCol = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const created = await onCreateCustomColumn({
                name: 'Nova coluna',
                expression: '',
                format: 'number',
            });
            const ref = idToCustomRef(created.id);
            onChange(['select', ...visible, ref]);
            setAdding(false);
            setEditingRef(ref);
        } catch (err) {
            console.error('[operacoes/customColumns/create]', err);
        } finally {
            setCreating(false);
        }
    };

    const saveCustomCol = async (id, patch) => {
        setSavingCustom(true);
        try {
            await onUpdateCustomColumn(id, patch);
        } catch (err) {
            console.error('[operacoes/customColumns/update]', err);
        } finally {
            setSavingCustom(false);
        }
    };

    // Excluir afeta TODOS os presets que referenciam — exigir confirmação
    // explícita (ConfirmModal com ênfase destrutiva; nome da coluna na
    // mensagem pra evitar cliques acidentais). O clique só arma o modal;
    // a exclusão de fato acontece em confirmDeleteCustomCol.
    const deleteCustomCol = (id) => setDeletingColId(id);

    const confirmDeleteCustomCol = async () => {
        const id = deletingColId;
        if (!id) return;
        setSavingCustom(true);
        try {
            await onDeleteCustomColumn(id);
            // Remove a ref do preset atual também.
            const ref = idToCustomRef(id);
            const next = visible.filter((c) => c !== ref);
            onChange(['select', ...next]);
            setEditingRef(null);
        } catch (err) {
            console.error('[operacoes/customColumns/delete]', err);
        } finally {
            setSavingCustom(false);
            setDeletingColId(null);
        }
    };

    return (
        <div>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="preset-cols">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {visible.length === 0 && (
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: T.subtle,
                                        fontStyle: 'italic',
                                        padding: '6px 0',
                                    }}
                                >
                                    Sem colunas. Adicione abaixo.
                                </div>
                            )}
                            {visible.map((col, i) => {
                                const key = ColKey(col);
                                const isCustom = isCustomRef(col);
                                const customDef = isCustom ? customColumnsById?.get(refToCustomId(col)) : null;
                                const label = ColDisplay(col, customColumnsById);
                                const isEditing = isCustom && editingRef === col;
                                const isMissing = isCustom && !customDef;
                                return (
                                    <Draggable key={key} draggableId={String(key)} index={i}>
                                        {(prov, snap) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                style={{
                                                    marginBottom: 4,
                                                    ...prov.draggableProps.style,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '6px 8px',
                                                        border: `1px solid ${
                                                            snap.isDragging ? T.accent : isMissing ? T.danger : T.line
                                                        }`,
                                                        borderRadius: 4,
                                                        background: snap.isDragging ? T.bgMid : T.bg,
                                                        boxShadow: snap.isDragging
                                                            ? '0 4px 12px rgba(0,0,0,0.10)'
                                                            : 'none',
                                                    }}
                                                >
                                                    <span
                                                        {...prov.dragHandleProps}
                                                        style={{
                                                            cursor: 'grab',
                                                            display: 'inline-flex',
                                                            color: T.subtle,
                                                        }}
                                                    >
                                                        <DotsSixVertical size={14} />
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            color: isMissing ? T.danger : T.ink,
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {label}
                                                    </span>
                                                    {isCustom && customDef && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingRef(isEditing ? null : col)}
                                                            title={
                                                                isEditing
                                                                    ? 'Fechar editor'
                                                                    : 'Editar fórmula (afeta todos os presets)'
                                                            }
                                                            style={{
                                                                background: isEditing ? T.accent : T.accentBg,
                                                                color: isEditing ? '#fff' : T.accent,
                                                                border: 'none',
                                                                borderRadius: 3,
                                                                padding: '2px 6px',
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 3,
                                                                outline: 'none',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            Editar fórmula
                                                        </button>
                                                    )}
                                                    {/* Toggle de visualização — text/bar pra QUALQUER coluna
                                                        numérica (inclusive custom com format number/%/$).
                                                        line (sparkline) só pras CHARTABLE_COLS (agregadas
                                                        com daily slots: sales7/30, week1..4, month1..3). */}
                                                    {isNumericColumn(col, customDef) && (
                                                        <ViewModeToggle
                                                            currentView={columnViews?.[key] || 'text'}
                                                            showLine={CHARTABLE_COLS.has(key)}
                                                            onChange={(next) => {
                                                                const nextMap = { ...(columnViews || {}) };
                                                                if (next === 'text') {
                                                                    delete nextMap[key];
                                                                } else {
                                                                    nextMap[key] = next;
                                                                }
                                                                onColumnViewsChange?.(nextMap);
                                                            }}
                                                        />
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCol(i)}
                                                        aria-label={`Remover ${label}`}
                                                        title="Remover deste preset (não exclui a coluna)"
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: T.muted,
                                                            padding: 2,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            outline: 'none',
                                                        }}
                                                    >
                                                        <X size={11} />
                                                    </button>
                                                </div>
                                                {isEditing && customDef && (
                                                    <ExpressionEditor
                                                        colDef={customDef}
                                                        saving={savingCustom}
                                                        onSave={(patch) => saveCustomCol(customDef.id, patch)}
                                                        onDelete={(id) => deleteCustomCol(id)}
                                                        onClose={() => setEditingRef(null)}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <div ref={addRef} style={{ position: 'relative' }}>
                <AddRow label="adicionar coluna" onClick={() => setAdding((v) => !v)} />
                {adding && (
                    <div
                        style={{
                            position: 'absolute',
                            zIndex: 30,
                            left: 0,
                            right: 0,
                            marginTop: 4,
                            background: T.bg,
                            border: `1px solid ${T.line}`,
                            borderRadius: 4,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                            maxHeight: 300,
                            overflowY: 'auto',
                            padding: 4,
                        }}
                    >
                        {availableBuiltins.length === 0 && availableCustoms.length === 0 && (
                            <div style={{ padding: '8px 10px', fontSize: 12, color: T.muted, fontStyle: 'italic' }}>
                                Todas adicionadas
                            </div>
                        )}
                        {availableBuiltins.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => addCol(c)}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: 12,
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: T.ink,
                                    outline: 'none',
                                }}
                            >
                                {colLabel(c)}
                            </button>
                        ))}
                        {availableCustoms.length > 0 && (
                            <div
                                style={{
                                    borderTop: `1px solid ${T.line}`,
                                    marginTop: 4,
                                    paddingTop: 4,
                                }}
                            >
                                <div
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        letterSpacing: 0.6,
                                        textTransform: 'uppercase',
                                        color: T.muted,
                                    }}
                                >
                                    Personalizadas (globais)
                                </div>
                                {availableCustoms.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => addCol(idToCustomRef(c.id))}
                                        title={c.expression || 'sem expressão'}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '6px 10px',
                                            fontSize: 12,
                                            textAlign: 'left',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: T.ink,
                                            outline: 'none',
                                        }}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* "Nova coluna customizada" sempre disponível. Cria
                            global via API + adiciona ref + abre editor inline. */}
                        <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 4, paddingTop: 4 }}>
                            <button
                                type="button"
                                onClick={addNewCustomCol}
                                disabled={creating || customColumnsLoading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: 12,
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: creating || customColumnsLoading ? 'wait' : 'pointer',
                                    color: T.accent,
                                    fontWeight: 600,
                                    outline: 'none',
                                    opacity: creating ? 0.6 : 1,
                                }}
                            >
                                <Plus size={11} />
                                {creating ? 'Criando…' : 'Nova coluna customizada'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmação de exclusão de coluna custom — substitui o antigo
                window.confirm. `loading` segura o modal aberto durante o DELETE. */}
            <ConfirmModal
                isOpen={Boolean(deletingColId)}
                onClose={() => {
                    if (!savingCustom) {
                        setDeletingColId(null);
                    }
                }}
                onConfirm={confirmDeleteCustomCol}
                loading={savingCustom}
                title="Excluir coluna personalizada"
                message={`Excluir "${customColumnsById?.get(deletingColId)?.name || 'esta coluna'}"? Ela será removida de TODOS os presets que a usam (não só deste).`}
                confirmText="Excluir"
                confirmVariant="danger"
            />
        </div>
    );
}

/**
 * ColumnSelector section — wrapper PSection do ColumnsList. Consome o context
 * pra pegar cols/customColumns/callbacks; o estado interno (adding/editing) é
 * local porque é puramente UI (não precisa propagar pro draft).
 */
export function ColumnSelector() {
    const {
        draft,
        patch,
        customColumns,
        customColumnsById,
        customColumnsLoading,
        onCreateCustomColumn,
        onUpdateCustomColumn,
        onDeleteCustomColumn,
    } = usePresetBuilder();

    const cols = Array.isArray(draft?.columns) ? draft.columns : [];

    return (
        <PSection
            title="Colunas"
            hint="Arraste pra reordenar. Em agregadas (sales, semanas, meses) clique '123/▮▮' pra alternar texto/barra."
        >
            <ColumnsList
                cols={cols}
                columnViews={draft?.columnViews || {}}
                onColumnViewsChange={(next) =>
                    patch({ columnViews: Object.keys(next).length === 0 ? undefined : next })
                }
                onChange={(nextCols) => patch({ columns: nextCols })}
                customColumns={customColumns}
                customColumnsById={customColumnsById}
                customColumnsLoading={customColumnsLoading}
                onCreateCustomColumn={onCreateCustomColumn}
                onUpdateCustomColumn={onUpdateCustomColumn}
                onDeleteCustomColumn={onDeleteCustomColumn}
            />
        </PSection>
    );
}
